import JSZip from "jszip";
import { getStorage, ref, getDownloadURL } from "firebase/storage";
import { type FactoringImage } from "./factoringModel";

export const downloadFactoringImages = async (
  selectedImages: FactoringImage[],
): Promise<void> => {
  if (selectedImages.length === 0) {
    console.warn("No images selected for download");
    return;
  }

  const zip = new JSZip();
  const storage = getStorage();

  // Keep track of filenames to handle duplicates
  const fileNameMap = new Map<string, number>();

  // Fetch all images and add them to the zip
  const fetchPromises = selectedImages.map(async (image) => {
    try {
      // Get download URL from Firebase Storage
      const imageRef = ref(storage, image.filePath);
      const url = await getDownloadURL(imageRef);

      // Fetch the image as a blob
      const response = await fetch(url);
      const blob = await response.blob();

      // Handle duplicate filenames
      let uniqueFileName = image.fileName;
      if (fileNameMap.has(image.fileName)) {
        // Get file extension and base name
        const lastDotIndex = image.fileName.lastIndexOf('.');
        const ext = lastDotIndex !== -1 ? image.fileName.substring(lastDotIndex) : '';
        const baseName = lastDotIndex !== -1 ? image.fileName.substring(0, lastDotIndex) : image.fileName;

        // Increment counter for this filename
        const count = fileNameMap.get(image.fileName)! + 1;
        fileNameMap.set(image.fileName, count);

        // Create new filename with counter
        uniqueFileName = `${baseName}_${count}${ext}`;
      } else {
        // First occurrence of this filename
        fileNameMap.set(image.fileName, 0);
      }

      // Add the image to the zip with a unique filename
      zip.file(uniqueFileName, blob);

      return { success: true, fileName: image.fileName, uniqueFileName };
    } catch (error) {
      console.error(`Error downloading image ${image.fileName}:`, error);
      return { success: false, fileName: image.fileName, uniqueFileName: image.fileName, error };
    }
  });

  // Wait for all fetch operations to complete
  const results = await Promise.all(fetchPromises);

  // Check if any images failed to download
  const failedImages = results.filter(result => !result.success);
  if (failedImages.length > 0) {
    console.warn(`Failed to download ${failedImages.length} images:`,
      failedImages.map(img => img.uniqueFileName || img.fileName).join(", "));
  }

  // Generate the zip file
  const zipBlob = await zip.generateAsync({ type: "blob" });

  // Create a download link and trigger the download
  const zipFileName = `factoring_images_${Date.now()}.zip`;

  const downloadLink = document.createElement("a");
  downloadLink.href = URL.createObjectURL(zipBlob);
  downloadLink.download = zipFileName;
  document.body.appendChild(downloadLink);
  downloadLink.click();

  // Clean up
  setTimeout(() => {
    document.body.removeChild(downloadLink);
    URL.revokeObjectURL(downloadLink.href);
  }, 100);
};
